import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronDown, Download, Search, X, Filter, Clock, Paperclip } from "lucide-react";
import { toast } from "sonner";
import {
  getNotas,
  getAtraso,
  getComprovantesResumo,
  formatCNPJ,
  formatDateTime,
  buildExportUrl,
} from "@/lib/ocorenApi";
import { CATEGORIAS, getCategoria } from "@/lib/categorias";
import { CategoriasGrid } from "@/components/CategoriasGrid";
import { NFTimeline } from "@/components/NFTimeline";
import { NotaActions } from "@/components/NotaActions";
import { ComprovantesDialog } from "@/components/ComprovantesDialog";

const EMPTY_FILTERS = {
  cnpj_transportadora: "",
  cnpj_cliente: "",
  numero_nota: "",
  categoria: "",
  data_inicio: "",
  data_fim: "",
};

const Field = ({ label, testId, type = "text", value, onChange, placeholder, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
      {label}
    </label>
    {children ? (
      children
    ) : (
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B] placeholder:text-[#A1A1AA]"
      />
    )}
  </div>
);

const StatusBadge = ({ categoriaKey }) => {
  const cat = getCategoria(categoriaKey);
  const Icon = cat.icon;
  return (
    <span
      data-testid={`nota-status-${categoriaKey}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${cat.bg} ${cat.text} ${cat.border}`}
    >
      <Icon size={11} />
      {cat.label}
    </span>
  );
};

export default function Notas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategoria = searchParams.get("categoria") || "";
  const initialAtraso = searchParams.get("atraso") === "1";
  const initialNumeroNota = searchParams.get("numero_nota") || "";

  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    categoria: initialCategoria,
    numero_nota: initialNumeroNota,
  });
  const [atrasoMode, setAtrasoMode] = useState(initialAtraso);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [compMap, setCompMap] = useState({});
  const [compNota, setCompNota] = useState(null);

  const loadCompResumo = useCallback(async () => {
    try {
      const r = await getComprovantesResumo();
      const map = {};
      (r.items || []).forEach((it) => {
        map[`${it.numero_nota}|${it.cnpj_cliente || ""}`] = it.total;
      });
      setCompMap(map);
    } catch {
      /* silencioso */
    }
  }, []);

  const fetchData = useCallback(
    async (f, atraso) => {
      setLoading(true);
      try {
        if (atraso) {
          const res = await getAtraso(true);
          setData(res);
        } else {
          const res = await getNotas({ ...f, limit: 200 });
          setData(res);
        }
        loadCompResumo();
      } catch {
        toast.error("Falha ao carregar notas");
      } finally {
        setLoading(false);
      }
    },
    [loadCompResumo]
  );

  useEffect(() => {
    fetchData(filters, atrasoMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) =>
    setFilters((prev) => ({ ...prev, [k]: e.target.value }));

  const applyFilters = () => fetchData(filters, atrasoMode);
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchData(EMPTY_FILTERS, atrasoMode);
  };

  const toggleAtraso = () => {
    const next = !atrasoMode;
    setAtrasoMode(next);
    const params = {};
    if (next) params.atraso = "1";
    else if (filters.categoria) params.categoria = filters.categoria;
    setSearchParams(params);
    fetchData(filters, next);
  };

  const onSelectCategoria = (key) => {
    const next = { ...filters, categoria: key || "" };
    setFilters(next);
    setAtrasoMode(false);
    if (key) setSearchParams({ categoria: key });
    else setSearchParams({});
    fetchData(next, false);
  };

  const onExport = () => {
    // Exporta movimentações (eventos) com filtros aplicados
    window.open(buildExportUrl(filters), "_blank");
  };

  return (
    <div data-testid="page-notas" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Visão por Nota
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Notas Fiscais
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            {atrasoMode
              ? `${data.total} NF(s) em atraso — prazo vencido sem confirmação de entrega.`
              : `${data.total} NF(s) únicas — uma linha por nota, com o último status e histórico expansível.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="notas-atraso-toggle"
            onClick={toggleAtraso}
            className={`px-4 py-2.5 text-sm font-semibold rounded-md flex items-center gap-2 border transition-colors ${
              atrasoMode
                ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                : "bg-white text-[#09090B] border-[#E4E4E7] hover:border-amber-400 hover:text-amber-600"
            }`}
          >
            <Clock size={15} />
            {atrasoMode ? "Mostrando: Em Atraso" : "Ver Em Atraso"}
          </button>
          <button
            data-testid="notas-export-btn"
            onClick={onExport}
            className="bg-[#09090B] text-white hover:bg-[#27272A] px-5 py-2.5 text-sm font-semibold rounded-md flex items-center gap-2 transition-colors"
          >
            <Download size={15} />
            Exportar Eventos
          </button>
        </div>
      </div>

      {/* Grid de categorias (clicável para drill-down) */}
      <div className="mb-6">
        <CategoriasGrid
          selected={filters.categoria || null}
          onSelect={onSelectCategoria}
        />
      </div>

      {/* Filtros */}
      <div
        data-testid="notas-filters"
        className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-[#52525B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
            Filtros
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Field
            label="CNPJ Cliente"
            testId="notas-filter-cnpj"
            value={filters.cnpj_cliente}
            onChange={onChange("cnpj_cliente")}
            placeholder="00.000.000/0000-00"
          />
          <Field
            label="Número NF"
            testId="notas-filter-nf"
            value={filters.numero_nota}
            onChange={onChange("numero_nota")}
            placeholder="000123456"
          />
          <Field label="Categoria">
            <select
              data-testid="notas-filter-categoria"
              value={filters.categoria}
              onChange={onChange("categoria")}
              className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
            >
              <option value="">Todas</option>
              {CATEGORIAS.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Data início"
            testId="notas-filter-data-inicio"
            type="date"
            value={filters.data_inicio}
            onChange={onChange("data_inicio")}
          />
          <Field
            label="Data fim"
            testId="notas-filter-data-fim"
            type="date"
            value={filters.data_fim}
            onChange={onChange("data_fim")}
          />
          <div className="flex items-end gap-2 lg:col-span-2">
            <button
              data-testid="notas-apply-btn"
              onClick={applyFilters}
              className="flex-1 bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              <Search size={14} />
              Filtrar
            </button>
            <button
              data-testid="notas-clear-btn"
              onClick={clearFilters}
              className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-3 py-2 text-sm font-medium rounded-md transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabela (Desktop/Tablet) */}
      <div className="hidden md:block bg-white border border-[#E4E4E7] rounded-md overflow-x-auto">
        <table className="data-table w-full min-w-[900px] text-sm" data-testid="notas-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>NF</th>
              <th>Série</th>
              <th>CNPJ Cliente</th>
              <th>Último status</th>
              <th>Última descrição</th>
              <th className="text-right">Eventos</th>
              <th>Última atualização</th>
              <th className="w-10 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  {atrasoMode
                    ? "Nenhuma NF em atraso. 🎉"
                    : "Nenhuma nota encontrada com os filtros atuais."}
                </td>
              </tr>
            )}
            {!loading &&
              data.items.map((n, idx) => {
                const key = `${n.numero_nota}-${n.cnpj_cliente}-${idx}`;
                const isExpanded = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      data-testid={`nota-row-${idx}`}
                      onClick={() => setExpanded(isExpanded ? null : key)}
                      className="cursor-pointer"
                    >
                      <td className="w-8 text-[#52525B]">
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td className="font-mono-data font-semibold text-[#09090B]">
                        <span className="inline-flex items-center gap-1.5">
                          {n.numero_nota || "—"}
                          {compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`] > 0 && (
                            <button
                              data-testid={`nota-comprovante-badge-${idx}`}
                              title={`${compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`]} comprovante(s) — clique para ver/baixar`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCompNota(n);
                              }}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer"
                            >
                              <Paperclip size={10} />
                              {compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`]}
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="font-mono-data text-xs text-[#52525B]">
                        {n.serie || "—"}
                      </td>
                      <td className="font-mono-data text-xs text-[#52525B]">
                        {formatCNPJ(n.cnpj_cliente)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <StatusBadge categoriaKey={n.ultima_categoria} />
                          {n.em_atraso && (
                            <span
                              data-testid={`nota-atraso-badge-${idx}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border bg-amber-50 text-amber-700 border-amber-200"
                            >
                              <Clock size={11} />
                              {n.dias_atraso}d
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-xs text-[#52525B] max-w-md truncate">
                        {n.ultima_descricao || "—"}
                      </td>
                      <td className="text-right">
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs font-bold font-mono-data bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                          {n.total_eventos}
                        </span>
                      </td>
                      <td className="text-xs text-[#52525B] font-mono-data whitespace-nowrap">
                        {formatDateTime(n.ultima_data)}
                      </td>
                      <td
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <NotaActions
                          nota={n}
                          idx={idx}
                          onChanged={() => fetchData(filters, atrasoMode)}
                          onOpenComprovantes={(nota) => setCompNota(nota)}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${key}-expanded`}>
                        <td colSpan={9} className="bg-[#FAFAFA] p-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-xs">
                            <div>
                              <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1">
                                Transportadora(s)
                              </div>
                              <div className="text-[#09090B]">
                                {(n.transportadoras || []).filter(Boolean).join(", ") || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1">
                                Arquivo(s) origem
                              </div>
                              <div className="text-[#09090B] font-mono-data">
                                {(n.arquivos || []).filter(Boolean).join(", ") || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1">
                                Primeiro evento
                              </div>
                              <div className="text-[#09090B] font-mono-data">
                                {formatDateTime(n.primeira_data)}
                              </div>
                            </div>
                          </div>
                          <NFTimeline
                            numero_nota={n.numero_nota}
                            cnpj_cliente={n.cnpj_cliente}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Cards (Mobile) */}
      <div className="block md:hidden space-y-4">
        {loading && (
          <div className="text-center text-[#52525B] py-10 bg-white border border-[#E4E4E7] rounded-md">
            Carregando…
          </div>
        )}
        {!loading && data.items.length === 0 && (
          <div className="text-center text-[#52525B] py-10 bg-white border border-[#E4E4E7] rounded-md">
            {atrasoMode
              ? "Nenhuma NF em atraso. 🎉"
              : "Nenhuma nota encontrada com os filtros atuais."}
          </div>
        )}
        {!loading &&
          data.items.map((n, idx) => {
            const key = `${n.numero_nota}-${n.cnpj_cliente}-${idx}`;
            const isExpanded = expanded === key;
            return (
              <div
                key={key}
                data-testid={`nota-card-${idx}`}
                className="bg-white border border-[#E4E4E7] rounded-md p-4 space-y-3"
              >
                {/* Cabeçalho do Card */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpanded(isExpanded ? null : key)}
                      className="p-1 text-[#52525B] hover:bg-gray-100 rounded"
                    >
                      {isExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </button>
                    <div>
                      <div className="font-mono-data font-semibold text-[#09090B] flex items-center gap-1.5">
                        <span>NF {n.numero_nota || "—"}</span>
                        {compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`] > 0 && (
                          <button
                            data-testid={`nota-card-comprovante-badge-${idx}`}
                            title={`${compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`]} comprovante(s) — clique para ver/baixar`}
                            onClick={() => setCompNota(n)}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer"
                          >
                            <Paperclip size={10} />
                            {compMap[`${n.numero_nota}|${n.cnpj_cliente || ""}`]}
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-[#52525B] font-mono-data mt-0.5">
                        Série: {n.serie || "—"} | CNPJ: {formatCNPJ(n.cnpj_cliente)}
                      </div>
                    </div>
                  </div>
                  {/* Ações */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <NotaActions
                      nota={n}
                      idx={idx}
                      onChanged={() => fetchData(filters, atrasoMode)}
                      onOpenComprovantes={(nota) => setCompNota(nota)}
                    />
                  </div>
                </div>

                {/* Status e Eventos */}
                <div className="flex items-center justify-between border-t border-[#F4F4F5] pt-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge categoriaKey={n.ultima_categoria} />
                    {n.em_atraso && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border bg-amber-50 text-amber-700 border-amber-200">
                        <Clock size={11} />
                        {n.dias_atraso}d
                      </span>
                    )}
                  </div>
                  <div className="text-[#52525B]">
                    Eventos:{" "}
                    <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs font-bold font-mono-data bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                      {n.total_eventos}
                    </span>
                  </div>
                </div>

                {/* Descrição e Data */}
                <div className="bg-[#FAFAFA] p-2 rounded text-xs space-y-1">
                  <div className="text-[#52525B] truncate">
                    <span className="font-semibold text-[#09090B]">Última descrição:</span>{" "}
                    {n.ultima_descricao || "—"}
                  </div>
                  <div className="text-[#52525B] font-mono-data text-[11px]">
                    <span className="font-semibold text-[#09090B]">Atualização:</span>{" "}
                    {formatDateTime(n.ultima_data)}
                  </div>
                </div>

                {/* Timeline expandida (Mobile) */}
                {isExpanded && (
                  <div className="border-t border-[#F4F4F5] pt-3 mt-3 bg-[#FAFAFA] -mx-4 -mb-4 p-4 rounded-b-md">
                    <div className="grid grid-cols-1 gap-3 text-xs mb-4">
                      <div>
                        <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1 text-[10px]">
                          Transportadora(s)
                        </div>
                        <div className="text-[#09090B]">
                          {(n.transportadoras || []).filter(Boolean).join(", ") || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1 text-[10px]">
                          Arquivo(s) origem
                        </div>
                        <div className="text-[#09090B] font-mono-data break-all">
                          {(n.arquivos || []).filter(Boolean).join(", ") || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-[0.15em] text-[#52525B] font-bold mb-1 text-[10px]">
                          Primeiro evento
                        </div>
                        <div className="text-[#09090B] font-mono-data">
                          {formatDateTime(n.primeira_data)}
                        </div>
                      </div>
                    </div>
                    <NFTimeline
                      numero_nota={n.numero_nota}
                      cnpj_cliente={n.cnpj_cliente}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <ComprovantesDialog
        open={!!compNota}
        onOpenChange={(o) => !o && setCompNota(null)}
        nota={compNota}
        onChanged={() => fetchData(filters, atrasoMode)}
      />
    </div>
  );
}
